import { useRef, useEffect, useCallback } from 'react';
import { Col, Row, Spinner, Table } from 'react-bootstrap';
import { useInfiniteQuery, useMutation } from 'react-query';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
    InvoiceFilterStatus,
    InvoiceStatus,
} from '../../../constants/invoiceStatus';
import { NotificationIcon } from '../../../constants/notificationIcon';
import { rowsPerPage } from '../../../constants/pagination';
import { Role } from '../../../constants/role';
import { useAuth } from '../../../contexts/authContext';
import useIntersectionObserver from '../../../hooks/useIntersectionObserver';
import { budgetsDetailRoute } from '../../../routes/config';
import InvoiceHttpService from '../../../services/http/invoice-http';
import ConfirmationDialog from '../../ConfirmationDialog';
import userHasRoles from '../../../utils/userHasRoles';
import ReleasePaymentMethodModal from '../../OnlineStore/Invoice/ReleasePaymentMethodModal';
import InvoiceApprovalModal from '../InvoiceApprovalModal';
import { StyledTh } from './styles';
import useInvoices from '../../../hooks/useInvoices';
import InvoiceTableRow from '../InvoiceTableRow';
import InvoiceCancelmentModal from '../InvoiceCancelmentModal';
import handleResponseError from '../../../utils/handleResponseError';
import InvoiceReturnModal from '../InvoiceReturnModal';

export default function InvoiceTable() {
    const history = useHistory();

    const { user } = useAuth();

    const {
        filterForm,
        filterTerm,
        selectedInvoice,
        setSelectedInvoice,
        showRemakeConfirmation,
        setShowRemakeConfirmation,
        showReproveConfirmation,
        setShowReproveConfirmation,
        showReproveValidatedConfirmation,
        setShowReproveValidatedConfirmation,
        showReturnConfirmation,
        setShowReturnConfirmation,
        showInvoiceCancelmentModal,
        setShowInvoiceCancelmentModal,
        showReleasePaymentMethodInvoiceModal,
        setShowReleasePaymentMethodInvoiceModal,
        showInvoiceApprovalModal,
        setShowInvoiceApprovalModal,
        setShowSendFretefyConfirmation,
        showSendFretefyConfirmation,
    } = useInvoices();

    const isIntegrator = userHasRoles(user, [
        Role.Integrator,
        Role.Contributor,
        Role.Manager,
    ]);

    const isAdminOrComSupOrCom = userHasRoles(user, [
        Role.Administrator,
        Role.CommercialSupervisor,
        Role.Commercial,
    ]);

    const canAccessAttachments = isAdminOrComSupOrCom;

    const canAccessPayment = isIntegrator || isAdminOrComSupOrCom;

    const formData = filterForm.watch();

    const invoicesQuery = useInfiniteQuery({
        queryKey: ['invoices', filterTerm, ...Object.values(formData)],
        queryFn: async ({ pageParam = 1 }) => {
            const filterStatusValues = formData.filterStatus
                ?.map((item) => item.value)
                .join(',');

            const defaultFilterStatusValues =
                Object.values(InvoiceFilterStatus).join(',') || null;

            const filterPaymentMethodValues = formData.filterPaymentMethod
                ?.map((item) => item.value)
                .join('/');

            const filterTriangulationStatusValues =
                formData.filterTriangulationStatus
                    ?.map((item) => item.value)
                    .join(',');

            const params = {
                term: filterTerm,
                skip: pageParam,
                take: rowsPerPage,
                modules: formData.filterModules || '',
                inverters: formData.filterInverters || '',
                orderby: formData.filterOrder?.value || '',
                ordertype: formData.filterOrderType?.value || '',
                futureSale: formData.filterFutureSale?.value ?? 99,
                status: filterStatusValues || defaultFilterStatusValues,
                regions: formData.filterRegion || [],
                sellers: formData.filterSeller || [],
                paymentMethods: filterPaymentMethodValues || '',
                triangulationStatus: filterTriangulationStatusValues || '',
                attachment: formData.filterAttachment?.value,
            };

            const response = await InvoiceHttpService.index(params);

            return {
                data: response.data.data,
                currentPage: pageParam,
                total: response.data.total,
            };
        },
        getNextPageParam: ({ total, currentPage }) =>
            total !== 0 ? currentPage + 1 : undefined,
        staleTime: 300000,
        refetchOnWindowFocus: false,
        refetchOnMount: 'always',
    });

    const target = useRef(undefined);

    useIntersectionObserver({
        target,
        onIntersect: invoicesQuery.fetchNextPage,
        enabled: invoicesQuery.hasNextPage,
    });

    const showLoading =
        invoicesQuery.isLoading ||
        invoicesQuery.isFetchingNextPage ||
        !invoicesQuery.isFetched;

    const remakeMutation = useMutation({
        mutationKey: ['remake-invoice'],
        mutationFn: async (keepAttachments: boolean) =>
            InvoiceHttpService.remake(
                selectedInvoice.id,
                keepAttachments,
                true,
            ),
        onError: (error: any) => {
            handleResponseError(error, 'Ocorreu um erro ao refazer o pedido');
            setShowRemakeConfirmation(false);
        },
        onSuccess: (data) => {
            toast.success('Pedido refeito com sucesso');
            history.push(budgetsDetailRoute.build(data.data));
        },
        onSettled: () => invoicesQuery.refetch(),
    });

    const cancelMutation = useMutation({
        mutationFn: async (reason: string) =>
            InvoiceHttpService.cancel(selectedInvoice.id, { reason }),
        onError: (error: any) => {
            handleResponseError(error, 'Ocorreu um erro ao cancelar o pedido.');
        },
        onSuccess: () => {
            setShowInvoiceCancelmentModal(false);
            toast.success('Pedido cancelado com sucesso!');
        },
        onSettled: () => {
            invoicesQuery.refetch();
            setSelectedInvoice(null);
        },
    });

    const invalidateMutation = useMutation({
        mutationFn: async (feedback: string) => {
            await InvoiceHttpService.invalidate(selectedInvoice.id.toString(), {
                feedback,
            });
        },
        onError: (error: any) => {
            handleResponseError(error, 'Ocorreu um erro ao reprovar o pedido.');
        },
        onSuccess: () => {
            toast.success('Pedido reprovado com sucesso');
        },
        onSettled: () => invoicesQuery.refetch(),
    });

    const reproveMutation = useMutation({
        mutationFn: (feedback: string) =>
            InvoiceHttpService.reprove(selectedInvoice.id.toString(), {
                observation: feedback,
            }),
        onError: (error) => {
            handleResponseError(error, 'Ocorreu um erro ao reprovar o pedido.');
        },
        onSuccess: async () => {
            invoicesQuery.refetch();
            toast.success('Pedido reprovado com sucesso');
        },
    });

    const sendFretefyQueueMutation = useMutation({
        mutationFn: async () => {
            await InvoiceHttpService.sendToFretefy(selectedInvoice.id);
        },
        onError: (error: any) => {
            handleResponseError(
                error,
                'Ocorreu um erro ao enviar o pedido para o Fretefy',
            );
        },
        onSuccess: () => {
            toast.info('Enviando pedido para o Fretefy');
        },
        onSettled: () => invoicesQuery.refetch(),
    });

    const invoices = invoicesQuery.data?.pages.reduce(
        (accumulator, page) => accumulator.concat(page.data),
        [],
    );

    const selectedInvoiceHasAttachments =
        selectedInvoice?.invoiceAttachments?.filter(
            (attachment: any) => !attachment.deletedAt,
        ).length;

    const checkStatusUpdates = useCallback(async () => {
        if (showLoading) return;

        const processing = invoices?.filter(
            (i) => i.status === InvoiceStatus.Processing,
        );

        if (!processing?.length) return;

        const response = await InvoiceHttpService.getMultipleInvoicesStatus(
            processing.map((i) => i.id),
        );

        const invoicesToUpdate = response.data?.filter(
            (item) => item.status !== InvoiceStatus.Processing,
        );

        if (invoicesToUpdate?.length && invoicesQuery) {
            const ids = invoicesToUpdate.map((i) => i.id);

            toast.info(`Atualizando status dos pedidos: ${ids.join(', ')}`);

            invoicesQuery.refetch();
        }
    }, [invoices, invoicesQuery, showLoading]);

    useEffect(() => {
        const interval = setInterval(checkStatusUpdates, 5000);

        return () => clearInterval(interval);
    }, [checkStatusUpdates]);

    return (
        <>
            <Row>
                <Col className="ml-2 mr-2">
                    <Table bordered hover size="sm" className="text-center">
                        <thead>
                            <tr>
                                <StyledTh>Nº DO PEDIDO</StyledTh>
                                <StyledTh>DATA DE APROVAÇÃO</StyledTh>
                                <StyledTh>CLIENTE</StyledTh>
                                {!isIntegrator && (
                                    <StyledTh>RESPONSÁVEL</StyledTh>
                                )}
                                <StyledTh>VALOR TOTAL</StyledTh>
                                {!isIntegrator && <StyledTh>FEEDBACK</StyledTh>}
                                {isIntegrator && (
                                    <StyledTh>PREVISÃO DE ENTREGA</StyledTh>
                                )}
                                {canAccessAttachments && (
                                    <StyledTh>COMPROVANTES</StyledTh>
                                )}
                                <StyledTh>STATUS</StyledTh>
                                {canAccessPayment && (
                                    <StyledTh>PAGAMENTO</StyledTh>
                                )}
                                <StyledTh />
                            </tr>
                        </thead>
                        <tbody>
                            {invoices?.map((invoice: any) => (
                                <InvoiceTableRow
                                    key={invoice.id}
                                    invoice={invoice}
                                />
                            ))}
                        </tbody>
                    </Table>
                </Col>

                <Col md={12} className="text-center" ref={target}>
                    {showLoading && (
                        <Col md={12} className="text-center">
                            <Spinner animation="border" />
                        </Col>
                    )}

                    {!showLoading && !invoices?.length && (
                        <Col md={12} className="text-center">
                            <strong style={{ color: '#adadad' }}>
                                Sem itens para carregar
                            </strong>
                        </Col>
                    )}
                </Col>
            </Row>

            {showInvoiceCancelmentModal && (
                <InvoiceCancelmentModal
                    show={showInvoiceCancelmentModal}
                    onHide={() => {
                        setShowInvoiceCancelmentModal(false);
                    }}
                    onCancel={(reason: string) => {
                        cancelMutation.mutateAsync(reason);
                    }}
                />
            )}

            {showInvoiceApprovalModal && (
                <InvoiceApprovalModal
                    show={showInvoiceApprovalModal}
                    onHide={() => setShowInvoiceApprovalModal(false)}
                    refetch={invoicesQuery.refetch}
                />
            )}

            {showReleasePaymentMethodInvoiceModal && (
                <ReleasePaymentMethodModal
                    show={showReleasePaymentMethodInvoiceModal}
                    onHide={() => {
                        invoicesQuery.refetch();
                        setShowReleasePaymentMethodInvoiceModal(false);
                    }}
                    invoice={selectedInvoice}
                />
            )}

            {showRemakeConfirmation && (
                <ConfirmationDialog
                    show={showRemakeConfirmation}
                    onHide={() => {
                        setShowRemakeConfirmation(false);
                    }}
                    onConfirmWithCheckbox={
                        selectedInvoiceHasAttachments
                            ? (keepAttachments) =>
                                  remakeMutation.mutateAsync(keepAttachments)
                            : undefined
                    }
                    onConfirm={
                        !selectedInvoiceHasAttachments
                            ? () => remakeMutation.mutateAsync(false)
                            : undefined
                    }
                    title="Refazer pedido"
                    text="Deseja refazer o pedido selecionado?"
                    icon={NotificationIcon.Info}
                    label="Desejo manter os anexos do pedido original"
                />
            )}

            {showReproveConfirmation && (
                <ConfirmationDialog
                    show={showReproveConfirmation}
                    onHide={() => setShowReproveConfirmation(false)}
                    onConfirmWithFeedback={async (feedback: string) =>
                        invalidateMutation.mutateAsync(feedback)
                    }
                    title="Reprovar pedido"
                    text="Deseja reprovar o pedido selecionado?"
                    icon={NotificationIcon.Warning}
                    placeholder="Insira o motivo da reprovação..."
                    required={false}
                />
            )}

            {showReproveValidatedConfirmation && (
                <ConfirmationDialog
                    show={showReproveValidatedConfirmation}
                    onHide={() => setShowReproveValidatedConfirmation(false)}
                    onConfirmWithFeedback={async (feedback: string) =>
                        reproveMutation.mutateAsync(feedback)
                    }
                    title="Reprovar pedido"
                    text="Deseja reprovar o pedido selecionado?"
                    icon={NotificationIcon.Warning}
                    placeholder="Insira o motivo da reprovação..."
                    required
                />
            )}

            {showReturnConfirmation && (
                <InvoiceReturnModal
                    show={showReturnConfirmation}
                    invoiceId={selectedInvoice?.id}
                    invoiceNf={selectedInvoice?.nf}
                    onHide={() => setShowReturnConfirmation(false)}
                />
            )}

            {showSendFretefyConfirmation && (
                <ConfirmationDialog
                    show={showSendFretefyConfirmation}
                    onHide={() => setShowSendFretefyConfirmation(false)}
                    onConfirm={async () => {
                        sendFretefyQueueMutation.mutateAsync();
                    }}
                    title="Enviar Pedido para Fretefy"
                    text="Deseja enviar pedido selecionado para o Fretefy?"
                    icon={NotificationIcon.Warning}
                />
            )}
        </>
    );
}
